import React, { useState, useEffect, useMemo } from "react";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Divider } from "@mui/material";
import Typography from "@mui/material/Typography";
import RefreshIcon from "@mui/icons-material/Refresh";
import Tooltip from "@mui/material/Tooltip";
import { Download } from "@mui/icons-material";
import MessageIcon from "@mui/icons-material/Message";
import FlagIcon from "@mui/icons-material/Flag";

// Hooks & Components
import { useAuth } from "../../hooks/AuthProvider";
import TableSearchInput from "../TableSearchInput";
import TableFilterButton from "../TableFilterButton";
import { FilterModal, FilterModalItem } from "../FilterModal";
import { CalendarIcon } from "@mui/x-date-pickers";
import ExportButton from "../ExportButton";
import ButtonTwo from "../../common/dashboard-button";
import ButtonOne from "../../common/normal-button";
import { ColorsConstants, searchBarWidth } from "../../cssUtils/constants";
import { ReactComponent as DropdownIcon } from "../../../src/assets/images/icons/logs/dropdown-arrow.svg";
import AppTable from "../../common/app-table";
import AppDropdown from "../../common/app-dropdown";
import {
  resolveLogPhoneSearchQueryForApi,
  normalizeCombinedSearchInput,
} from "../../utils/phoneSearch";
import { loadFilters, saveFilters, FILTER_NAMESPACES } from "../../utils/filterStorage";
import { formatWithTzLabel } from "../../utils/dateUtils";

// URL Configs - Make sure to define these in your config/urls.js
import {
  RCS_CHAT_LOG_ENDPOINT,
  RCS_CHAT_LOG_EXPORT_ENDPOINT,
} from "../../config/urls";

const tableColumns = [
  {
    id: "sender",
    label: "From",
    minWidth: 160,
    sortable: true,
  },
  {
    id: "receiver",
    label: "To",
    minWidth: 160,
    sortable: true,
  },
  {
    id: "logged_at",
    label: "Date",
    minWidth: 180,
    sortable: true,
    render: (message) =>
      message.logged_at ? formatWithTzLabel(message.logged_at, "MMMM D, YYYY, h:mm A") : "N/A",
  },
  {
    id: "status",
    label: "Status",
    minWidth: 120,
    sortable: true,
    render: (message) => (
      <Chip
        label={message.status?.toUpperCase() || "UNKNOWN"}
        size="small"
        sx={{
          fontWeight: 500,
          backgroundColor: message.status === 'received' ? '#e3f2fd' :
                           message.status === 'delivered' ? '#e8f5e9' :
                           message.status === 'failed' ? '#ffebee' : '#f5f5f5',
          color: message.status === 'received' ? '#1565c0' :
                 message.status === 'delivered' ? '#2e7d32' :
                 message.status === 'failed' ? '#c62828' : '#616161'
        }}
      />
    )
  },
  {
    id: "message_type",
    label: "Message Type",
    minWidth: 160,
    sortable: true,
  },
  {
    id: "message",
    label: "Message / Payload",
    minWidth: 320,
    render: (message) => message.message || <i>Media/System Event</i>
  },
];

export default function RcsChatLogTable({
  refreshKey,
  controller,
  setController,
  lastSynced,
  setLastSynced,
}) {
  const [rowCount, setRowCount] = useState(0);
  const [messageData, setmessageData] = useState([]);
  
  const [savedFilters] = useState(() => {
    const s = loadFilters(FILTER_NAMESPACES.RCS_CHAT_LOGS, {
      searchQuery: '', statusFilter: '', messageTypeFilter: '', fromDate: null, toDate: null,
    });
    return {
      ...s,
      fromDate: s.fromDate ? dayjs(s.fromDate) : null,
      toDate: s.toDate ? dayjs(s.toDate) : null,
    };
  });
  const [searchQuery, setSearchQuery] = useState(savedFilters.searchQuery);
  const [charCount, setCharCount] = useState(savedFilters.searchQuery.length);
  const [statusFilter, setStatusFilter] = useState(savedFilters.statusFilter);
  const [messageTypeFilter, setMessageTypeFilter] = useState(savedFilters.messageTypeFilter);
  const [fromDate, setFromDate] = useState(savedFilters.fromDate);
  const [toDate, setToDate] = useState(savedFilters.toDate);
  const [dateError, setDateError] = useState(null);

  const { getValidToken } = useAuth();

  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const [appliedDateRange, setAppliedDateRange] = useState([savedFilters.fromDate, savedFilters.toDate]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [exportState, setExportState] = useState("default");
  const [sortState, setSortState] = useState([]);

  const resolvedRcsChatSearch = useMemo(
    () => resolveLogPhoneSearchQueryForApi(searchQuery),
    [searchQuery],
  );

  const sortedData = useMemo(() => {
    if (!sortState.length || !messageData.length) return messageData;
    return [...messageData].sort((a, b) => {
      for (const { id, direction } of sortState) {
        let aVal = a[id];
        let bVal = b[id];
        if (aVal == null && bVal == null) continue;
        if (aVal == null) return direction === "asc" ? 1 : -1;
        if (bVal == null) return direction === "asc" ? -1 : 1;
        if (typeof aVal === "string") { aVal = aVal.toLowerCase(); bVal = String(bVal).toLowerCase(); }
        if (aVal < bVal) return direction === "asc" ? -1 : 1;
        if (aVal > bVal) return direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [messageData, sortState]);

  useEffect(() => {
    saveFilters(FILTER_NAMESPACES.RCS_CHAT_LOGS, {
      searchQuery,
      statusFilter,
      messageTypeFilter,
      fromDate: fromDate ? dayjs(fromDate).toISOString() : null,
      toDate: toDate ? dayjs(toDate).toISOString() : null,
    });
  }, [searchQuery, statusFilter, messageTypeFilter, fromDate, toDate]);

  const buildQueryParams = () => {
    const queryParams = new URLSearchParams({
      limit: controller.rowsPerPage.toString(),
      offset: (controller.serverSidePage * controller.rowsPerPage).toString(),
    });

    // Uses standard DRF generic view filters (SearchFilter & DjangoFilterBackend)
    if (resolvedRcsChatSearch.apiSearchQuery) {
      queryParams.append("search", resolvedRcsChatSearch.apiSearchQuery);
    }
    if (statusFilter) queryParams.append("status", statusFilter);
    if (messageTypeFilter) queryParams.append("message_type", messageTypeFilter);
    if (fromDate) queryParams.append("logged_at__gte", dayjs(fromDate).format("YYYY-MM-DDT00:00:00"));
    if (toDate) queryParams.append("logged_at__lte", dayjs(toDate).format("YYYY-MM-DDT23:59:59"));

    return queryParams;
  };

  const fetchData = async () => {
    if (resolvedRcsChatSearch.blockFetch) {
      setmessageData([]);
      setRowCount(0);
      setLastSynced(null);
      return;
    }
    const queryParams = buildQueryParams();
    const tenant = localStorage.getItem("tenant");
    const token = await getValidToken();

    const headers = {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    try {
      const response = await fetch(`${RCS_CHAT_LOG_ENDPOINT}?${queryParams}`, { headers });
      if (!response.ok) throw new Error("Failed to fetch data");
      
      const jsonData = await response.json();
      setmessageData(jsonData.results || []);
      setRowCount(jsonData.count || 0);
      
      if (jsonData.results?.length > 0) {
        setLastSynced(formatWithTzLabel(jsonData.results[0].created_at, "MMMM D, YYYY, h:mm:ss A"));
      }
    } catch (error) {
      console.error("Error fetching data:", error.message);
    }
  };

  const handleExport = async () => {
    if (resolvedRcsChatSearch.blockFetch) {
      setExportState("failed");
      return;
    }
    try {
      setExportState("loading");
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");
      const queryParams = buildQueryParams();

      const response = await fetch(`${RCS_CHAT_LOG_EXPORT_ENDPOINT}?${queryParams}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-Id": tenant,
        },
      });

      if (!response.ok) throw new Error("Failed to export data");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;

      const dateStr = new Date().toISOString().split("T")[0];
      a.download = `rcs-chat-log-${dateStr}.csv`;
      document.body.appendChild(a);
      a.click();
      setExportState("success");
      
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error exporting data:", error.message);
      setExportState("failed");
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    controller.serverSidePage,
    controller.rowsPerPage,
    refreshKey,
    searchQuery,
    statusFilter,
    messageTypeFilter,
    fromDate,
    toDate,
  ]);

  useEffect(() => {
    setAppliedDateRange([fromDate, toDate]);
  }, []);

  const handleDateRangeApply = () => {
    setFromDate(appliedDateRange[0]);
    setToDate(appliedDateRange[1]);
    setDateAnchorEl(null);
    setController({ page: 0, rowsPerPage: controller.rowsPerPage, serverSidePage: 0 });
  };

  const handleDateRangeCancel = () => {
    setAppliedDateRange([fromDate, toDate]);
    setDateAnchorEl(null);
  };

  const handleMessageTypeSelect = (type) => {
    setMessageTypeFilter(messageTypeFilter === type ? "" : type);
    setController({ page: 0, rowsPerPage: controller.rowsPerPage, serverSidePage: 0 });
  };

  const handleStatusSelect = (status) => {
    setStatusFilter(statusFilter === status ? "" : status);
    setController({ page: 0, rowsPerPage: controller.rowsPerPage, serverSidePage: 0 });
  };

  const updateSearchValue = (value) => {
    const normalized = normalizeCombinedSearchInput(value);
    if (normalized.length > 50) return;
    setSearchQuery(normalized);
    setCharCount(normalized.length);
    setController({ page: 0, rowsPerPage: controller.rowsPerPage, serverSidePage: 0 });
  };

  const handlePageChange = (event, newPage) => {
    setController((prev) => ({ ...prev, page: newPage, serverSidePage: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    setController((prev) => ({
      ...prev,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0,
      serverSidePage: 0,
    }));
  };

  const handleResetButtonClick = () => {
    setSearchQuery("");
    setStatusFilter("");
    setMessageTypeFilter("");
    setFromDate(null);
    setToDate(null);
    setAppliedDateRange([null, null]);
    setSortState([]);
    setController({ page: 0, rowsPerPage: controller.rowsPerPage, serverSidePage: 0 });
  };

  const handleSortChange = (columnId) => {
    setSortState((prev) => {
      const existing = prev.find((s) => s.id === columnId);
      if (!existing) return [...prev, { id: columnId, direction: "asc" }];
      if (existing.direction === "asc") return prev.map((s) => s.id === columnId ? { ...s, direction: "desc" } : s);
      return prev.filter((s) => s.id !== columnId);
    });
  };

  const getTotalActiveFilters = () => {
    let count = 0;
    if (statusFilter) count++;
    if (messageTypeFilter) count++;
    if (fromDate) count++;
    if (toDate) count++;
    return count;
  };

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", backgroundColor: "#F7F7F7", border: "none", boxShadow: "none" }}>
      {/* Mobile Header */}
      <Box sx={{ display: { xs: "flex", md: "none" }, flexDirection: "column", gap: 2, mb: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <ButtonOne variant="contained" startIcon={<Download />} onClick={handleExport}>
            Export
          </ButtonOne>
        </Box>
        <Divider />
        <Box sx={{ display: "flex", gap: 1 }}>
          <TableSearchInput
            searchQuery={searchQuery}
            onSearchChange={(e) => updateSearchValue(e.target.value)}
            charCount={charCount}
            tooltip="Search Logs"
            error={Boolean(resolvedRcsChatSearch.blockFetch && resolvedRcsChatSearch.phoneSearchError)}
            helperText={resolvedRcsChatSearch.blockFetch ? resolvedRcsChatSearch.phoneSearchError : undefined}
          />
          <TableFilterButton
            setFilterModalOpen={setFilterModalOpen}
            totalActiveFilters={getTotalActiveFilters()}
          />
        </Box>
      </Box>

      {/* Desktop Header */}
      <Box sx={{ display: { xs: "none", md: "flex" }, flexDirection: "column", p: 0, m: 1, borderRadius: 1 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", overflowX: "auto", pb: 1 }}>
          <Box sx={{ width: searchBarWidth }}>
            <TableSearchInput
              searchQuery={searchQuery}
              onSearchChange={(e) => updateSearchValue(e.target.value)}
              charCount={charCount}
              tooltip="Search message, sender, or receiver"
              placeholder="Search"
              error={Boolean(resolvedRcsChatSearch.blockFetch && resolvedRcsChatSearch.phoneSearchError)}
              helperText={resolvedRcsChatSearch.blockFetch ? resolvedRcsChatSearch.phoneSearchError : undefined}
            />
          </Box>

          <AppDropdown
            endIcon={<DropdownIcon />}
            sx={{ whiteSpace: "nowrap" }}
            paperProps={{ maxWidth: "500px" }}
            menuContent={(closeMenu) => (
              <Box sx={{ p: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Typography variant="subtitle2">Select Log Date:</Typography>  
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <DatePicker
                        label="From"
                        value={appliedDateRange[0]}
                        onChange={(val) => setAppliedDateRange([val, appliedDateRange[1]])}
                        slotProps={{ textField: { size: "small", sx: { width: "200px" } } }}
                      />
                      <DatePicker
                        label="To"
                        value={appliedDateRange[1]}
                        onChange={(val) => setAppliedDateRange([appliedDateRange[0], val])}
                        slotProps={{ textField: { size: "small", sx: { width: "200px" } } }}
                      />
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                      <ButtonTwo onClick={() => { handleDateRangeCancel(); closeMenu(); }} variant="outlined" title="CANCEL" />
                      <ButtonOne onClick={() => { handleDateRangeApply(); closeMenu(); }} variant="contained" title="Apply" />
                    </Box>
                  </Box>
                </LocalizationProvider>
              </Box>
            )}
          >
            Log Date
            {fromDate && toDate && (
              <Chip
                label={`${dayjs(fromDate).format("MM/DD/YY")} - ${dayjs(toDate).format("MM/DD/YY")}`}
                size="small"
                onDelete={() => { setFromDate(null); setToDate(null); }}
                sx={{ ml: 1 }}
              />
            )}
          </AppDropdown>

          <AppDropdown
            endIcon={<DropdownIcon />}
            menuItems={[
              { label: "Text", value: "TEXT" },
              { label: "Image", value: "IMAGE" },
              { label: "Video", value: "VIDEO" },
              { label: "File", value: "FILE" },
              { label: "Suggestion / Button", value: "SUGGESTION_RESPONSE" },
              { label: "Location", value: "LOCATION" },
            ]}
            selectedValue={messageTypeFilter}
            onMenuSelect={(value) => handleMessageTypeSelect(value)}
          >
            Message Type
            {messageTypeFilter && (
              <Chip label={messageTypeFilter} size="small" onDelete={() => handleMessageTypeSelect(messageTypeFilter)} sx={{ ml: 1 }} />
            )}
          </AppDropdown>

          <AppDropdown
            endIcon={<DropdownIcon />}
            menuItems={[
              { label: "Received", value: "received" },
              { label: "Sent", value: "sent" },
              { label: "Delivered", value: "delivered" },
              { label: "Read", value: "read" },
              { label: "Failed", value: "failed" },
              { label: "Is Typing", value: "IS_TYPING" },
            ]}
            selectedValue={statusFilter}
            onMenuSelect={(value) => handleStatusSelect(value)}
          >
            Status
            {statusFilter && (
              <Chip label={statusFilter} size="small" onDelete={() => handleStatusSelect(statusFilter)} sx={{ ml: 1 }} />
            )}
          </AppDropdown>

          <ButtonTwo
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleResetButtonClick}
            sx={{ height: "40px", borderRadius: "8px", border: `1px solid ${ColorsConstants.primaryBackgroundColor}`, color: ColorsConstants.primaryBackgroundColor }}
          >
            Reset
          </ButtonTwo>

          <Box>
            <ExportButton handleExport={handleExport} state={exportState} setState={setExportState} />
          </Box>
        </Box>
      </Box>

      {/* Table Section */}
      <AppTable
        columns={tableColumns}
        rows={sortedData}
        rowKey={(message, index) => message.id || index}
        emptyMessage="No RCS logs found"
        maxHeight={500}
        containerSx={{
          scrollbarWidth: "thin",
          scrollbarColor: "#C5CEDB #F3F6FB",
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-track": { backgroundColor: "#F3F6FB", borderRadius: "8px" },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "#C5CEDB", borderRadius: "8px" },
          "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#A8B4C7" },
        }}
        count={rowCount}
        page={controller.page}
        rowsPerPage={controller.rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sortState={sortState}
        onSortChange={handleSortChange}
      />

      <FilterModal open={filterModalOpen} onClose={() => setFilterModalOpen(false)} onReset={handleResetButtonClick}>
        <FilterModalItem
          name="Message Type"
          data={["TEXT", "IMAGE", "VIDEO", "FILE", "SUGGESTION_RESPONSE", "LOCATION"]}
          value={messageTypeFilter}
          onChange={(e) => handleMessageTypeSelect(e.target.value)}
          icon={<MessageIcon />}
        />
        <FilterModalItem
          name="Status"
          data={["received", "sent", "delivered", "read", "failed", "IS_TYPING"]}
          value={statusFilter}
          onChange={(e) => handleStatusSelect(e.target.value)}
          icon={<FlagIcon />}
        />
      </FilterModal>
    </Paper>
  );
}